import React, { useEffect, useRef, useState } from 'react';
import api, { getApiErrorMessage } from '../api';
import ThemePicker from './ThemePicker';
import ShareSettings from './presentations/ShareSettings';
import {
    IMAGE_TYPES, NO_OPTION_TYPES, OTHER_TYPES, QUESTION_TYPES, QuestionTypeIcon, SWITCHABLE_TYPES, typeLabel,
} from './questionTypes';
import './EditPollModal.css';

const UPGRADE_NOTE = 'Для активации и редактирования опросов необходимо перейти на другой тариф.';

/** Текст ошибки сервера; при 403 — подсказка про тариф. */
const apiError = (err, fallback) => {
    const detail = getApiErrorMessage(err, fallback);
    return err?.response?.status === 403 ? `${detail} ${UPGRADE_NOTE}` : detail;
};

const imageSrc = (id) => (id.startsWith('http') || id.startsWith('/static') ? id : `/proxy/image/${id}`);

const EMPTY_QUESTION = {
    text: '',
    type: 'single',
    options: ['', ''],
    image_id: null,
    required: false,
    other_option: false,
    other_label: '',
    moderation_enabled: true,
    moderation_strictness: 'medium',
};

/** Переключатель-тумблер: сохраняет сразу, подпись и пояснение рядом. */
function Toggle({ checked, onChange, disabled, label, hint, title }) {
    return (
        <label className={`ep-toggle${disabled ? ' is-disabled' : ''}`} title={title}>
            <input type="checkbox" role="switch" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
            <span className="ep-toggle__track" aria-hidden />
            <span className="ep-toggle__text">
                <span>{label}</span>
                {hint && <small>{hint}</small>}
            </span>
        </label>
    );
}

const Icon = ({ d }) => (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden focusable="false">
        <path d={d} />
    </svg>
);
const ICONS = {
    up: 'M12 19V5M5 12l7-7 7 7',
    down: 'M12 5v14M19 12l-7 7-7-7',
    edit: 'M4 20h4L19 9l-4-4L4 16v4zM13.5 6.5l4 4',
    trash: 'M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3',
    image: 'M4 5h16v14H4zM4 15l4-4 4 4 3-3 5 5M15 9.5h.01',
};

/** Переключатель «Другое» в форме нового вопроса: что это и как будет выглядеть у участника. */
function OtherOptionField({ enabled, label, onToggle, onLabel, multiple }) {
    const name = label.trim() || 'Другое';
    return (
        <div className={`edit-poll-other${enabled ? ' is-on' : ''}`}>
            <label className="edit-poll-other__toggle">
                <input type="checkbox" checked={enabled} onChange={(e) => onToggle(e.target.checked)} />
                <span>
                    <b>Добавить вариант «Другое» со своим ответом</b>
                    <small>
                        Участник выберет его{multiple ? ' (можно вместе с другими вариантами)' : ''} и напишет ответ строкой — до 100 символов.
                    </small>
                </span>
            </label>
            {enabled && (
                <div className="edit-poll-other__body">
                    <label className="edit-poll-other__label">
                        <span>Название варианта</span>
                        <input type="text" value={label} maxLength={60} placeholder="Другое" onChange={(e) => onLabel(e.target.value)} />
                    </label>
                    <div className="edit-poll-other__preview" aria-hidden>
                        <small>Так увидит участник:</small>
                        <div className="edit-poll-other__option">
                            <span className={`edit-poll-other__mark${multiple ? ' is-square' : ''}`} />
                            <span>{name}</span>
                        </div>
                        <div className="edit-poll-other__input">Напишите свой вариант…</div>
                    </div>
                    <ul className="edit-poll-other__facts">
                        <li>На графике «{name}» — отдельный столбец с числом выбравших.</li>
                        <li>Сами ответы показываются под графиком облаком фраз: одинаковые объединяются, частые — крупнее.</li>
                        <li>Каждый ответ проходит автопроверку: грубость и спам на экран не попадут. В Excel выгружаются все ответы.</li>
                    </ul>
                </div>
            )}
        </div>
    );
}

const WORDCLOUD_MODES = [
    {
        value: 'phrases',
        title: 'Целыми фразами',
        hint: 'Ответ показывается так, как его написали. Одинаковые ответы объединяются и растут. Для «Что запомнилось?», «Ваши ожидания».',
        sample: [['Много практики', 3], ['Полезно', 2], ['Хочу ещё', 1]],
    },
    {
        value: 'words',
        title: 'По словам',
        hint: 'Ответы делятся на слова, служебные («и», «очень», «было») отбрасываются, формы одного слова объединяются. Для «Опишите одним словом».',
        sample: [['практика', 3], ['полезно', 2], ['ещё', 1]],
    },
];

/** Выбор подсчёта облака: карточки с маленьким примером того, как будет выглядеть облако. */
function WordcloudModePicker({ value, onChange }) {
    return (
        <div className="wc-mode" role="radiogroup" aria-label="Как показывать ответы в облаке">
            <span className="wc-mode__label">Как показывать ответы</span>
            <div className="wc-mode__options">
                {WORDCLOUD_MODES.map((m) => (
                    <button
                        key={m.value}
                        type="button"
                        role="radio"
                        aria-checked={value === m.value}
                        className={`wc-mode__option${value === m.value ? ' is-active' : ''}`}
                        onClick={() => onChange(m.value)}
                    >
                        <span className="wc-mode__sample" aria-hidden>
                            {m.sample.map(([text, n]) => (
                                <span key={text} style={{ fontSize: `${11 + n * 3}px` }}>{text}</span>
                            ))}
                        </span>
                        <b>{m.title}</b>
                        <small>{m.hint}</small>
                    </button>
                ))}
            </div>
            <small className="wc-mode__note">Можно поменять и после запуска — облако пересчитается по уже полученным ответам.</small>
        </div>
    );
}

const OPTION_EDIT_TYPES = ['single', 'multiple', 'ranking'];

/**
 * Варианты ответа в карточке вопроса. Пока на вопрос никто не ответил, на платных тарифах
 * их можно переименовать, переставить, добавить и удалить. «Другое» включается отдельно.
 */
function QuestionOptions({ question, userTier, onSaved }) {
    const [draft, setDraft] = useState(null); // null — просмотр; массив — режим правки
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const regular = (question.options || []).filter((o) => !o.is_other);
    const other = (question.options || []).find((o) => o.is_other);
    const editableType = OPTION_EDIT_TYPES.includes(question.type || 'single');
    const answered = (question.answers_count || 0) > 0;
    const tierLocked = userTier === 'free';

    const start = () => {
        setDraft(regular.map((o) => ({ id: o.id, text: o.text, key: `o${o.id}` })));
        setError('');
    };
    const update = (i, text) => setDraft((d) => d.map((o, j) => (j === i ? { ...o, text } : o)));
    const remove = (i) => setDraft((d) => d.filter((_, j) => j !== i));
    const move = (i, delta) => setDraft((d) => {
        const next = [...d];
        const [item] = next.splice(i, 1);
        next.splice(i + delta, 0, item);
        return next;
    });
    const add = () => setDraft((d) => [...d, { id: null, text: '', key: `n${Date.now()}${d.length}` }]);

    const save = async () => {
        const filled = draft.filter((o) => o.text.trim());
        if (filled.length < 2) {
            setError('Нужно хотя бы 2 варианта ответа.');
            return;
        }
        setSaving(true);
        setError('');
        try {
            const res = await api.put(`/questions/${question.id}/options`, {
                options: filled.map((o) => ({ id: o.id, text: o.text.trim() })),
            });
            onSaved(res.data.options);
            setDraft(null);
        } catch (err) {
            setError(getApiErrorMessage(err, 'Не удалось сохранить варианты'));
        } finally {
            setSaving(false);
        }
    };

    if (draft) {
        return (
            <div className="options-edit">
                <strong>Варианты ответов</strong>
                <ol className="options-edit__list">
                    {draft.map((o, i) => (
                        <li key={o.key} className="options-edit__row">
                            <input
                                type="text"
                                value={o.text}
                                maxLength={500}
                                autoFocus={!o.id && i === draft.length - 1}
                                placeholder={`Вариант ${i + 1}`}
                                aria-label={`Вариант ${i + 1}`}
                                onChange={(e) => update(i, e.target.value)}
                            />
                            <button type="button" className="options-edit__icon" onClick={() => move(i, -1)} disabled={i === 0} title="Выше" aria-label="Выше">↑</button>
                            <button type="button" className="options-edit__icon" onClick={() => move(i, 1)} disabled={i === draft.length - 1} title="Ниже" aria-label="Ниже">↓</button>
                            <button type="button" className="options-edit__icon options-edit__icon--danger" onClick={() => remove(i)} disabled={draft.length <= 2} title="Удалить вариант" aria-label="Удалить вариант">✕</button>
                        </li>
                    ))}
                    {other && (
                        <li className="options-edit__row options-edit__row--other">
                            <span>{other.text}</span>
                            <span className="edit-poll-other-badge">свой ответ</span>
                            <small>всегда последний, включается флажком «Вариант „Другое“»</small>
                        </li>
                    )}
                </ol>
                {draft.length < 100 && (
                    <button type="button" className="options-edit__add" onClick={add}>+ Добавить вариант</button>
                )}
                {error && <p className="options-edit__error" role="alert">{error}</p>}
                <div className="options-edit__actions">
                    <button type="button" className="btn btn--primary btn--sm" onClick={save} disabled={saving}>{saving ? 'Сохраняем…' : 'Сохранить варианты'}</button>
                    <button type="button" className="btn btn--secondary btn--sm" onClick={() => setDraft(null)} disabled={saving}>Отмена</button>
                </div>
            </div>
        );
    }

    let lockHint = null;
    if (editableType && answered) {
        lockHint = `На вопрос уже ответили (${question.answers_count}) — варианты менять нельзя, чтобы не исказить результаты. Очистите ответы опроса или добавьте новый вопрос.`;
    } else if (editableType && tierLocked) {
        lockHint = 'Менять варианты после сохранения можно на тарифах «Расширенный» и «Корпоративный».';
    }

    return (
        <div className="options-view">
            <ul className={`options-view__list options-view__list--${question.type === 'multiple' ? 'multi' : question.type === 'ranking' ? 'rank' : 'single'}`}>
                {question.options.map((opt) => (
                    <li key={opt.id}>
                        <span className="options-view__mark" aria-hidden />
                        <span>{opt.text}</span>
                        {opt.is_other && <span className="edit-poll-other-badge" title="Участник выбирает этот вариант и пишет свой ответ">свой ответ</span>}
                    </li>
                ))}
            </ul>
            {editableType && !answered && !tierLocked && (
                <button type="button" className="options-view__edit" onClick={start}>Изменить варианты</button>
            )}
            {lockHint && <p className="options-view__lock">{lockHint}</p>}
        </div>
    );
}

/** Форма нового вопроса: тип карточками, настройки типа, картинка, варианты. Ошибки — прямо в форме. */
function NewQuestionForm({ number, saving, onSubmit, onCancel }) {
    const [data, setData] = useState(EMPTY_QUESTION);
    const [error, setError] = useState('');
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const formRef = useRef(null);
    const textRef = useRef(null);
    const optionRefs = useRef([]);
    const focusOption = useRef(null);
    const set = (patch) => {
        setData((prev) => ({ ...prev, ...patch }));
        setError('');
    };

    useEffect(() => {
        formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        textRef.current?.focus({ preventScroll: true });
    }, []);

    // Новый вариант по Enter — фокус сразу в нём
    useEffect(() => {
        if (focusOption.current == null) return;
        optionRefs.current[focusOption.current]?.focus();
        focusOption.current = null;
    }, [data.options.length]);

    const needsImage = IMAGE_TYPES.includes(data.type);
    const hasOptions = !NO_OPTION_TYPES.includes(data.type);

    const upload = async (file) => {
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setError('Это не картинка. Подойдут JPG, PNG или WebP.');
            return;
        }
        const formData = new FormData();
        formData.append('file', file);
        setUploading(true);
        setError('');
        try {
            const res = await api.post('/upload/image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
            set({ image_id: res.data.image_id });
        } catch (err) {
            setError(getApiErrorMessage(err, 'Не удалось загрузить картинку'));
        } finally {
            setUploading(false);
        }
    };

    const updateOption = (index, value) => set({ options: data.options.map((o, i) => (i === index ? value : o)) });
    const removeOption = (index) => set({ options: data.options.filter((_, i) => i !== index) });
    const addOption = () => {
        focusOption.current = data.options.length;
        set({ options: [...data.options, ''] });
    };

    const submit = async () => {
        if (!data.text.trim()) {
            setError('Введите текст вопроса.');
            textRef.current?.focus();
            return;
        }
        if (needsImage && !data.image_id) {
            setError(data.type === 'grid'
                ? 'Для сетки загрузите картинку — участники будут выбирать на ней ячейку.'
                : 'Для тепловой карты загрузите картинку — участники будут отмечать на ней точки.');
            return;
        }
        if (hasOptions && data.options.filter((o) => o.trim()).length < 2) {
            setError('Добавьте хотя бы 2 варианта ответа.');
            return;
        }
        const message = await onSubmit(data);
        if (message) setError(message);
    };

    return (
        <section className="ep-new" ref={formRef} aria-label="Новый вопрос">
            <header className="ep-new__head">
                <span className="ep-q__num">{String(number).padStart(2, '0')}</span>
                <h4>Новый вопрос</h4>
            </header>

            <label className="ep-field">
                <span className="ep-field__label">Текст вопроса</span>
                <textarea
                    ref={textRef}
                    className="form-textarea"
                    value={data.text}
                    maxLength={2000}
                    onChange={(e) => set({ text: e.target.value })}
                    rows={2}
                    placeholder="Например: Что было самым полезным на занятии?"
                />
            </label>

            <div className="ep-field">
                <span className="ep-field__label" id="ep-type-label">Тип вопроса</span>
                <div className="ep-types" role="radiogroup" aria-labelledby="ep-type-label">
                    {QUESTION_TYPES.map((t) => (
                        <button
                            key={t.value}
                            type="button"
                            role="radio"
                            aria-checked={data.type === t.value}
                            className={`ep-type${data.type === t.value ? ' is-active' : ''}`}
                            onClick={() => set({ type: t.value })}
                        >
                            <QuestionTypeIcon type={t.value} />
                            <b>{t.label}</b>
                            <small>{t.hint}</small>
                        </button>
                    ))}
                </div>
            </div>

            {data.type === 'number' && (
                <div className="ep-row">
                    <label className="ep-field">
                        <span className="ep-field__label">Минимум <small>необязательно</small></span>
                        <input
                            type="number" step="any" className="form-input" placeholder="—"
                            value={data.min_value ?? ''}
                            onChange={(e) => set({ min_value: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
                        />
                    </label>
                    <label className="ep-field">
                        <span className="ep-field__label">Максимум <small>необязательно</small></span>
                        <input
                            type="number" step="any" className="form-input" placeholder="—"
                            value={data.max_value ?? ''}
                            onChange={(e) => set({ max_value: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
                        />
                    </label>
                </div>
            )}

            {data.type === 'rating' && (
                <div className="ep-field">
                    <span className="ep-field__label">Шкала</span>
                    <div className="seg-switch" role="radiogroup" aria-label="Шкала оценки">
                        {[5, 10].map((n) => (
                            <button key={n} type="button" role="radio" aria-checked={(data.max_value || 5) === n}
                                    className={(data.max_value || 5) === n ? 'is-active' : ''} onClick={() => set({ max_value: n })}>
                                {n} звёзд
                            </button>
                        ))}
                    </div>
                    <small className="ep-field__hint">Для шкалы 10 в отчёте считается NPS, для шкалы 5 — доля довольных.</small>
                </div>
            )}

            {data.type === 'wordcloud' && (
                <div className="ep-panel">
                    <WordcloudModePicker value={data.wordcloud_mode || 'phrases'} onChange={(mode) => set({ wordcloud_mode: mode })} />
                    <Toggle
                        checked={data.moderation_enabled ?? true}
                        onChange={(checked) => set({ moderation_enabled: checked })}
                        label="Премодерация ответов"
                        hint="Грубость и спам не попадут на экран: ответ сначала проверяется."
                    />
                    {(data.moderation_enabled ?? true) && (
                        <div className="ep-field">
                            <span className="ep-field__label">Строгость проверки</span>
                            <div className="seg-switch" role="radiogroup" aria-label="Строгость проверки">
                                {[['low', 'Мягкая'], ['medium', 'Средняя'], ['high', 'Строгая']].map(([v, l]) => (
                                    <button key={v} type="button" role="radio" aria-checked={(data.moderation_strictness || 'medium') === v}
                                            className={(data.moderation_strictness || 'medium') === v ? 'is-active' : ''}
                                            onClick={() => set({ moderation_strictness: v })}>
                                        {l}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    {data.wordcloud_mode === 'words' && (
                        <div className="ep-row ep-row--3">
                            <label className="ep-field">
                                <span className="ep-field__label">Слово от, букв</span>
                                <input type="number" min="1" className="form-input" value={data.wordcloud_min_len ?? 2}
                                       title="Слова короче не попадут в облако"
                                       onChange={(e) => set({ wordcloud_min_len: parseInt(e.target.value, 10) || 1 })} />
                            </label>
                            <label className="ep-field">
                                <span className="ep-field__label">Слово до, букв</span>
                                <input type="number" min="2" className="form-input" value={data.wordcloud_max_len ?? 30}
                                       title="Слишком длинные слова будут отброшены"
                                       onChange={(e) => set({ wordcloud_max_len: parseInt(e.target.value, 10) || 30 })} />
                            </label>
                            <label className="ep-field">
                                <span className="ep-field__label">Слов в ответе, до</span>
                                <input type="number" min="1" className="form-input" value={data.wordcloud_max_words_per_response ?? 10}
                                       title="Лишние слова будут обрезаны"
                                       onChange={(e) => set({ wordcloud_max_words_per_response: parseInt(e.target.value, 10) || 10 })} />
                            </label>
                        </div>
                    )}
                </div>
            )}

            {data.type === 'heatmap' && (
                <div className="ep-panel">
                    <p className="ep-panel__text">
                        Участники нажимают на картинку и ставят точку — её можно передвинуть до отправки.
                        На экране результатов клики складываются в тепловую карту, а популярные места
                        сервис сам выделяет зонами с процентами.
                    </p>
                    <div className="ep-field">
                        <span className="ep-field__label">Сколько точек ставит участник</span>
                        <div className="seg-switch" role="radiogroup" aria-label="Сколько точек ставит участник">
                            {[[1, 'Одну'], [2, 'До двух'], [3, 'До трёх']].map(([n, l]) => (
                                <button key={n} type="button" role="radio" aria-checked={(data.heatmap_max_clicks || 1) === n}
                                        className={(data.heatmap_max_clicks || 1) === n ? 'is-active' : ''}
                                        onClick={() => set({ heatmap_max_clicks: n })}>
                                    {l}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {data.type === 'grid' && (
                <div className="ep-row">
                    <label className="ep-field">
                        <span className="ep-field__label">Строк</span>
                        <input type="number" min="1" max="10" className="form-input" value={data.grid_rows || 3}
                               onChange={(e) => set({ grid_rows: Math.min(10, parseInt(e.target.value, 10) || 1) })} />
                    </label>
                    <label className="ep-field">
                        <span className="ep-field__label">Столбцов</span>
                        <input type="number" min="1" max="10" className="form-input" value={data.grid_cols || 3}
                               onChange={(e) => set({ grid_cols: Math.min(10, parseInt(e.target.value, 10) || 1) })} />
                    </label>
                </div>
            )}

            <div className="ep-field">
                <span className="ep-field__label">
                    Картинка {needsImage ? <small className="is-required">обязательно</small> : <small>необязательно</small>}
                </span>
                {data.image_id ? (
                    <div className="ep-image">
                        <img src={imageSrc(data.image_id)} alt="Картинка к вопросу" />
                        <div className="ep-image__actions">
                            <label className="btn btn--secondary btn--sm">
                                Заменить
                                <input type="file" accept="image/*" hidden onChange={(e) => upload(e.target.files[0])} />
                            </label>
                            <button type="button" className="btn btn--ghost btn--sm" onClick={() => set({ image_id: null })}>Убрать</button>
                        </div>
                    </div>
                ) : (
                    <label
                        className={`ep-drop${dragOver ? ' is-over' : ''}`}
                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files[0]); }}
                    >
                        <Icon d={ICONS.image} />
                        <span>{uploading ? 'Загружаем…' : <><b>Выберите картинку</b> или перетащите её сюда</>}</span>
                        <small>JPG, PNG или WebP</small>
                        <input type="file" accept="image/*" hidden disabled={uploading} onChange={(e) => upload(e.target.files[0])} />
                    </label>
                )}
            </div>

            {hasOptions && (
                <div className="ep-field">
                    <span className="ep-field__label">Варианты ответа</span>
                    <ol className={`ep-options ep-options--${data.type === 'multiple' ? 'multi' : data.type === 'ranking' ? 'rank' : 'single'}`}>
                        {data.options.map((opt, idx) => (
                            <li key={idx}>
                                <span className="options-view__mark" aria-hidden />
                                <input
                                    ref={(el) => { optionRefs.current[idx] = el; }}
                                    type="text"
                                    className="form-input"
                                    value={opt}
                                    maxLength={500}
                                    onChange={(e) => updateOption(idx, e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            if (idx === data.options.length - 1) addOption();
                                            else optionRefs.current[idx + 1]?.focus();
                                        }
                                    }}
                                    placeholder={`Вариант ${idx + 1}`}
                                    aria-label={`Вариант ${idx + 1}`}
                                />
                                <button type="button" className="ep-icon-btn ep-icon-btn--danger" onClick={() => removeOption(idx)}
                                        disabled={data.options.length <= 2} title="Удалить вариант" aria-label={`Удалить вариант ${idx + 1}`}>
                                    <Icon d={ICONS.trash} />
                                </button>
                            </li>
                        ))}
                    </ol>
                    <button type="button" className="ep-add-option" onClick={addOption}>+ Добавить вариант</button>
                    <small className="ep-field__hint">Enter — перейти к следующему варианту или добавить новый.</small>
                </div>
            )}

            {hasOptions && OTHER_TYPES.includes(data.type) && (
                <OtherOptionField
                    enabled={Boolean(data.other_option)}
                    label={data.other_label || ''}
                    onToggle={(checked) => set({ other_option: checked })}
                    onLabel={(value) => set({ other_label: value })}
                    multiple={data.type === 'multiple'}
                />
            )}

            <Toggle
                checked={Boolean(data.required)}
                onChange={(checked) => set({ required: checked })}
                label="Обязательный вопрос"
                hint="Участник не сможет перейти дальше и отправить ответы, пока не ответит."
            />

            {error && <p className="ep-error" role="alert">{error}</p>}

            <div className="ep-new__actions">
                <button type="button" className="btn btn--secondary" onClick={onCancel} disabled={saving}>Отмена</button>
                <button type="button" className="btn btn--primary" onClick={submit} disabled={saving || uploading}>
                    {saving ? 'Сохраняем…' : 'Добавить вопрос'}
                </button>
            </div>
        </section>
    );
}

/** Описание настроек вопроса одной строкой: диапазон, шкала, облако, точки. */
function questionMeta(q) {
    switch (q.type) {
        case 'number':
            return q.min_value != null || q.max_value != null ? `Диапазон: от ${q.min_value ?? '—'} до ${q.max_value ?? '—'}` : 'Любое число';
        case 'rating':
            return `Шкала: ${q.max_value || 5} звёзд`;
        case 'wordcloud':
            return `Премодерация ${q.moderation_enabled ? 'включена' : 'выключена'}`;
        case 'heatmap':
            return `Точек на участника: до ${q.heatmap_max_clicks || 1}`;
        case 'grid':
            return `Сетка ${q.grid_rows || 3} × ${q.grid_cols || 3}`;
        default:
            return '';
    }
}

const EditPollModal = ({ poll, onClose, onUpdate, userTier }) => {
    const [pollData, setPollData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null); // { id, text }
    const [infoDraft, setInfoDraft] = useState(null); // { title, description } — правка названия
    const [addingQuestion, setAddingQuestion] = useState(false);
    const [notice, setNotice] = useState('');
    const paid = userTier === 'advanced' || userTier === 'corporate';

    useEffect(() => {
        api.get(`/polls/${poll.id}`)
            .then((res) => setPollData(res.data))
            .catch((err) => console.error('Failed to load poll', err))
            .finally(() => setLoading(false));
    }, [poll.id]);

    // Пока открыта форма или правка текста, окно не закрывается случайно (Escape, клик мимо окна)
    const busyEditing = addingQuestion || Boolean(infoDraft) || Boolean(editingQuestion);
    const closeIfIdle = () => {
        if (!saving && !busyEditing) onClose();
    };
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape' && !saving && !busyEditing) onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose, saving, busyEditing]);

    const updateQuestion = (id, patch) => setPollData((prev) => ({
        ...prev,
        questions: prev.questions.map((q) => (q.id === id ? { ...q, ...patch } : q)),
    }));

    /** Общая обёртка сохранения: блокировка кнопок, ошибка — плашкой над вопросами. */
    const run = async (action, fallback) => {
        setSaving(true);
        setNotice('');
        try {
            await action();
            onUpdate();
            return true;
        } catch (err) {
            console.error(fallback, err);
            setNotice(apiError(err, fallback));
            return false;
        } finally {
            setSaving(false);
        }
    };

    const handleThemeChange = async (theme) => {
        if (!pollData || pollData.theme === theme) return;
        const previous = pollData.theme;
        setPollData({ ...pollData, theme });
        const ok = await run(() => api.patch(`/polls/${poll.id}`, { theme }), 'Не удалось изменить оформление');
        if (!ok) setPollData((prev) => ({ ...prev, theme: previous }));
    };

    const handleShowResults = async (show) => {
        setPollData((prev) => ({ ...prev, show_results: show }));
        const ok = await run(() => api.patch(`/polls/${poll.id}`, { show_results: show }), 'Не удалось изменить настройку');
        if (!ok) setPollData((prev) => ({ ...prev, show_results: !show }));
    };

    const saveInfo = async () => {
        if (!infoDraft.title.trim()) {
            setNotice('Введите название опроса.');
            return;
        }
        await run(async () => {
            const res = await api.patch(`/polls/${poll.id}`, { title: infoDraft.title.trim(), description: infoDraft.description });
            setPollData((prev) => ({ ...prev, title: res.data.title, description: res.data.description }));
            setInfoDraft(null);
        }, 'Ошибка при обновлении опроса');
    };

    const saveQuestionText = async () => {
        const { id, text } = editingQuestion;
        if (!text.trim()) {
            setNotice('Текст вопроса не может быть пустым.');
            return;
        }
        await run(async () => {
            await api.patch(`/questions/${id}`, { text: text.trim() });
            updateQuestion(id, { text: text.trim() });
            setEditingQuestion(null);
        }, 'Ошибка при обновлении текста вопроса');
    };

    const handleRequiredToggle = (id, required) => run(async () => {
        await api.patch(`/questions/${id}`, { required });
        updateQuestion(id, { required });
    }, 'Ошибка при изменении вопроса');

    const handleWordcloudModeChange = (id, mode) => run(async () => {
        await api.patch(`/questions/${id}`, { wordcloud_mode: mode });
        updateQuestion(id, { wordcloud_mode: mode });
    }, 'Не удалось изменить вид облака');

    const handleOtherToggle = (id, enabled) => run(async () => {
        const res = await api.patch(`/questions/${id}`, { other_option: enabled });
        updateQuestion(id, { options: res.data.options });
    }, 'Не удалось изменить вариант «Другое»');

    const handleQuestionTypeChange = (id, type) => run(async () => {
        const res = await api.patch(`/questions/${id}`, { type, allow_multiple: type === 'multiple' });
        updateQuestion(id, { type, allow_multiple: type === 'multiple', options: res.data?.options });
    }, 'Ошибка при обновлении типа вопроса');

    const handleDeleteQuestion = async (question, index) => {
        const answered = question.answers_count > 0 ? ` На него уже ответили (${question.answers_count}) — эти ответы тоже удалятся.` : '';
        if (!window.confirm(`Удалить вопрос ${index + 1} «${question.text.slice(0, 80)}»?${answered} Это действие нельзя отменить.`)) return;
        await run(async () => {
            await api.delete(`/questions/${question.id}`);
            setPollData((prev) => ({ ...prev, questions: prev.questions.filter((q) => q.id !== question.id) }));
        }, 'Ошибка при удалении вопроса');
    };

    const moveQuestion = async (index, delta) => {
        const list = [...pollData.questions];
        const [item] = list.splice(index, 1);
        list.splice(index + delta, 0, item);
        const previous = pollData.questions;
        setPollData((prev) => ({ ...prev, questions: list }));
        const ok = await run(
            () => api.put(`/polls/${poll.id}/questions/order`, { question_ids: list.map((q) => q.id) }),
            'Не удалось изменить порядок вопросов',
        );
        if (!ok) setPollData((prev) => ({ ...prev, questions: previous }));
    };

    /** Сохраняет новый вопрос; при ошибке возвращает текст для формы. */
    const addQuestion = async (data) => {
        const maxOrder = Math.max(...pollData.questions.map((q) => q.order || 0), -1);
        const type = data.type;
        const payload = {
            text: data.text.trim(),
            type,
            allow_multiple: type === 'multiple',
            image_id: data.image_id,
            grid_rows: type === 'grid' ? (data.grid_rows || 3) : null,
            grid_cols: type === 'grid' ? (data.grid_cols || 3) : null,
            min_value: type === 'number' ? (data.min_value ?? null) : (type === 'rating' ? 1 : null),
            max_value: (type === 'number' || type === 'rating') ? (data.max_value ?? (type === 'rating' ? 5 : null)) : null,
            wordcloud_min_len: type === 'wordcloud' ? (data.wordcloud_min_len ?? 2) : null,
            wordcloud_max_len: type === 'wordcloud' ? (data.wordcloud_max_len ?? 30) : null,
            wordcloud_max_words_per_response: type === 'wordcloud' ? (data.wordcloud_max_words_per_response ?? 10) : null,
            wordcloud_mode: data.wordcloud_mode || 'phrases',
            moderation_enabled: type === 'wordcloud' ? (data.moderation_enabled ?? true) : false,
            moderation_strictness: type === 'wordcloud' ? (data.moderation_strictness || 'medium') : null,
            required: Boolean(data.required),
            heatmap_max_clicks: type === 'heatmap' ? (data.heatmap_max_clicks || 1) : null,
            other_option: OTHER_TYPES.includes(type) && Boolean(data.other_option),
            other_label: data.other_label?.trim() || null,
            order: maxOrder + 1,
            // варианты сетки создаёт сервер, у остальных типов без вариантов их нет
            options: NO_OPTION_TYPES.includes(type)
                ? []
                : data.options.filter((o) => o.trim()).map((o, idx) => ({ text: o.trim(), order: idx })),
        };
        setSaving(true);
        try {
            const res = await api.post(`/polls/${poll.id}/questions`, payload);
            setPollData((prev) => ({ ...prev, questions: [...prev.questions, res.data] }));
            setAddingQuestion(false);
            onUpdate();
            return null;
        } catch (err) {
            console.error('Failed to add question', err);
            return apiError(err, 'Ошибка при добавлении вопроса');
        } finally {
            setSaving(false);
        }
    };

    if (loading || !pollData) {
        return (
            <div className="app-modal-overlay" role="presentation" onClick={onClose}>
                <div className="app-modal app-modal--wide" role="dialog" onClick={(e) => e.stopPropagation()}>
                    <div className="app-modal__body app-modal__body--center" style={{ padding: 'var(--space-10)' }}>
                        {loading ? 'Загрузка…' : 'Не удалось загрузить опрос. Закройте окно и попробуйте ещё раз.'}
                    </div>
                </div>
            </div>
        );
    }

    const questions = pollData.questions || [];
    const showResults = pollData.show_results ?? true;

    return (
        <div className="app-modal-overlay" role="presentation" onClick={closeIfIdle}>
            <div className="app-modal app-modal--wide ep" role="dialog" aria-labelledby="ep-title" onClick={(e) => e.stopPropagation()}>
                <div className="app-modal__header">
                    <h2 id="ep-title">Редактирование опроса</h2>
                    <button type="button" className="app-modal__close" onClick={onClose} aria-label="Закрыть">✕</button>
                </div>

                <div className="app-modal__body">
                    {notice && (
                        <div className="ep-notice" role="alert">
                            <span>{notice}</span>
                            <button type="button" onClick={() => setNotice('')} aria-label="Скрыть">✕</button>
                        </div>
                    )}

                    {/* ─── Опрос: название, описание, настройки ─── */}
                    <section className="ep-card" aria-labelledby="ep-info-title">
                        <div className="ep-card__head">
                            <span className="ep-kicker" id="ep-info-title">Опрос</span>
                            {!infoDraft && (
                                <button type="button" className="ep-link-btn" onClick={() => setInfoDraft({ title: pollData.title || '', description: pollData.description || '' })}>
                                    <Icon d={ICONS.edit} /> Изменить название
                                </button>
                            )}
                        </div>
                        {infoDraft ? (
                            <div className="ep-info-edit">
                                <label className="ep-field">
                                    <span className="ep-field__label">Название</span>
                                    <input className="form-input" value={infoDraft.title} maxLength={500} autoFocus
                                           onChange={(e) => setInfoDraft({ ...infoDraft, title: e.target.value })} />
                                </label>
                                <label className="ep-field">
                                    <span className="ep-field__label">Описание <small>необязательно</small></span>
                                    <textarea className="form-textarea" rows={2} value={infoDraft.description} maxLength={5000}
                                              placeholder="Пара слов для участников: о чём опрос"
                                              onChange={(e) => setInfoDraft({ ...infoDraft, description: e.target.value })} />
                                </label>
                                <div className="ep-inline-actions">
                                    <button type="button" className="btn btn--primary btn--sm" onClick={saveInfo} disabled={saving}>Сохранить</button>
                                    <button type="button" className="btn btn--secondary btn--sm" onClick={() => setInfoDraft(null)} disabled={saving}>Отмена</button>
                                </div>
                            </div>
                        ) : (
                            <div className="ep-info">
                                <h3>{pollData.title}</h3>
                                {pollData.description ? <p>{pollData.description}</p> : <p className="ep-muted">Без описания</p>}
                            </div>
                        )}

                        <div className="ep-settings">
                            <Toggle
                                checked={showResults}
                                onChange={handleShowResults}
                                disabled={saving || !paid}
                                label="Показывать результаты после ответа"
                                hint={paid
                                    ? (showResults ? 'Участник сразу увидит графики.' : 'Участник увидит «Спасибо за участие!» без результатов.')
                                    : 'Отключить можно на тарифах «Расширенный» и «Корпоративный».'}
                            />
                            <ThemePicker id="edit-poll-theme" value={pollData.theme || 'light'} disabled={saving} onChange={handleThemeChange} />
                            <ShareSettings
                                kind="poll" id={poll.id} paid={paid}
                                value={{ enabled: pollData.share_enabled, presentation_id: pollData.share_presentation_id, qr: pollData.share_qr }}
                                onChange={(share) => {
                                    setPollData((prev) => ({ ...prev, share_enabled: share.enabled, share_presentation_id: share.presentation_id, share_qr: share.qr }));
                                    onUpdate();
                                }}
                            />
                        </div>
                    </section>

                    {/* ─── Вопросы ─── */}
                    <section className="ep-questions" aria-labelledby="ep-questions-title">
                        <div className="ep-questions__head">
                            <h3 id="ep-questions-title">Вопросы <span className="ep-count">{questions.length}</span></h3>
                            {!addingQuestion && questions.length > 0 && (
                                <button type="button" className="btn btn--primary btn--sm" onClick={() => setAddingQuestion(true)} disabled={saving}>
                                    + Добавить вопрос
                                </button>
                            )}
                        </div>

                        {questions.length === 0 && !addingQuestion && (
                            <div className="ep-empty">
                                <b>В опросе пока нет вопросов</b>
                                <span>Добавьте первый — участники смогут ответить, когда вы активируете опрос.</span>
                                <button type="button" className="btn btn--primary" onClick={() => setAddingQuestion(true)}>+ Добавить вопрос</button>
                            </div>
                        )}

                        <ol className="ep-list">
                            {questions.map((question, index) => {
                                const type = question.type || 'single';
                                const isEditing = editingQuestion?.id === question.id;
                                const meta = questionMeta(question);
                                const hasOther = Boolean(question.options?.some((o) => o.is_other));
                                return (
                                    <li key={question.id} className={`ep-q${isEditing ? ' is-editing' : ''}`}>
                                        <div className="ep-q__rail">
                                            <span className="ep-q__num" aria-label={`Вопрос ${index + 1}`}>{String(index + 1).padStart(2, '0')}</span>
                                            {questions.length > 1 && (
                                                <div className="ep-q__move">
                                                    <button type="button" className="ep-icon-btn" onClick={() => moveQuestion(index, -1)}
                                                            disabled={saving || index === 0} title="Поднять выше" aria-label="Поднять вопрос выше">
                                                        <Icon d={ICONS.up} />
                                                    </button>
                                                    <button type="button" className="ep-icon-btn" onClick={() => moveQuestion(index, 1)}
                                                            disabled={saving || index === questions.length - 1} title="Опустить ниже" aria-label="Опустить вопрос ниже">
                                                        <Icon d={ICONS.down} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        <div className="ep-q__main">
                                            <div className="ep-q__chips">
                                                <span className="ep-chip ep-chip--type"><QuestionTypeIcon type={type} />{typeLabel(type)}</span>
                                                {question.required && <span className="ep-chip ep-chip--required" title="Без ответа на этот вопрос опрос не отправить">Обязательный</span>}
                                                {question.answers_count > 0 && <span className="ep-chip">Ответов: {question.answers_count}</span>}
                                            </div>

                                            {isEditing ? (
                                                <div className="ep-q__edit">
                                                    <textarea className="form-textarea" rows={2} value={editingQuestion.text} maxLength={2000} autoFocus
                                                              aria-label="Текст вопроса"
                                                              onChange={(e) => setEditingQuestion({ ...editingQuestion, text: e.target.value })}
                                                              onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) saveQuestionText(); }} />
                                                    <div className="ep-inline-actions">
                                                        <button type="button" className="btn btn--primary btn--sm" onClick={saveQuestionText} disabled={saving}>Сохранить</button>
                                                        <button type="button" className="btn btn--secondary btn--sm" onClick={() => setEditingQuestion(null)} disabled={saving}>Отмена</button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <h4 className="ep-q__title">{question.text}</h4>
                                            )}

                                            {meta && <p className="ep-q__meta">{meta}</p>}
                                            {question.image_id && IMAGE_TYPES.includes(type) && (
                                                <img className="ep-q__thumb" src={imageSrc(question.image_id)} alt="Картинка вопроса" />
                                            )}

                                            {question.options && question.options.length > 0 && type !== 'grid' && (
                                                <QuestionOptions
                                                    question={question}
                                                    userTier={userTier}
                                                    onSaved={(options) => {
                                                        updateQuestion(question.id, { options });
                                                        onUpdate();
                                                    }}
                                                />
                                            )}

                                            <div className="ep-q__toolbar">
                                                <Toggle checked={Boolean(question.required)} disabled={saving}
                                                        onChange={(checked) => handleRequiredToggle(question.id, checked)} label="Обязательный" />
                                                {OTHER_TYPES.includes(type) && (
                                                    <Toggle checked={hasOther} disabled={saving}
                                                            onChange={(checked) => handleOtherToggle(question.id, checked)}
                                                            label="Вариант «Другое»" title="Участник выбирает «Другое» и пишет свой ответ строкой" />
                                                )}
                                                {type === 'wordcloud' && (
                                                    <div className="seg-switch" role="radiogroup" aria-label="Как показывать ответы в облаке"
                                                         title="Можно менять в любой момент — облако пересчитается по уже полученным ответам">
                                                        {[['phrases', 'Фразами'], ['words', 'По словам']].map(([v, l]) => (
                                                            <button key={v} type="button" role="radio" aria-checked={(question.wordcloud_mode || 'words') === v}
                                                                    className={(question.wordcloud_mode || 'words') === v ? 'is-active' : ''} disabled={saving}
                                                                    onClick={() => handleWordcloudModeChange(question.id, v)}>
                                                                {l}
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                                {SWITCHABLE_TYPES.includes(type) && (
                                                    <label className="ep-select" title="Варианты ответа сохранятся">
                                                        <span>Тип</span>
                                                        <select value={type} disabled={saving} onChange={(e) => handleQuestionTypeChange(question.id, e.target.value)}>
                                                            {QUESTION_TYPES.filter((t) => SWITCHABLE_TYPES.includes(t.value)).map((t) => (
                                                                <option key={t.value} value={t.value}>{t.label}</option>
                                                            ))}
                                                        </select>
                                                    </label>
                                                )}
                                                <span className="ep-q__spacer" />
                                                {!isEditing && (
                                                    <button type="button" className="ep-icon-btn" onClick={() => setEditingQuestion({ id: question.id, text: question.text })}
                                                            disabled={saving} title="Изменить текст вопроса" aria-label="Изменить текст вопроса">
                                                        <Icon d={ICONS.edit} />
                                                    </button>
                                                )}
                                                <button type="button" className="ep-icon-btn ep-icon-btn--danger" onClick={() => handleDeleteQuestion(question, index)}
                                                        disabled={saving} title="Удалить вопрос" aria-label="Удалить вопрос">
                                                    <Icon d={ICONS.trash} />
                                                </button>
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>

                        {addingQuestion ? (
                            <NewQuestionForm
                                number={questions.length + 1}
                                saving={saving}
                                onSubmit={addQuestion}
                                onCancel={() => setAddingQuestion(false)}
                            />
                        ) : questions.length > 0 && (
                            <button type="button" className="ep-add" onClick={() => setAddingQuestion(true)} disabled={saving}>
                                + Добавить вопрос
                            </button>
                        )}
                    </section>
                </div>

                <div className="app-modal__footer ep-footer">
                    <span className="ep-footer__note">{saving ? 'Сохраняем…' : 'Изменения сохраняются сразу'}</span>
                    <button type="button" className="btn btn--secondary" onClick={onClose}>Готово</button>
                </div>
            </div>
        </div>
    );
};

export default EditPollModal;
